import React, { useMemo } from 'react';

import { useStoreContext } from 'features/store';

import { IBackendCartEntries } from '@rbi-ctg/menu';
import { AvailabilityStatus } from 'components/availability-status';
import FavoriteEntryButton from 'components/favorite-entry-button';
import { Product } from 'components/product';
import { ItemAvailabilityStatus } from 'enums/menu';
import { ICartEntryFragment } from 'generated/graphql-gateway';
import { useGetItemImageQuery } from 'generated/sanity-graphql';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import useFoodware from 'hooks/use-foodware';
import { IReorderEntry } from 'pages/menu/hooks/types';
import { CustomEventNames } from 'state/amplitude';
import { ILocalizedMenuEntry, IndexedMenu } from 'state/unified-menu/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { IMenuObjectWithDaypart, getMenuItemDayParts } from 'utils/availability';
import { getSelectionsListFromEntry } from 'utils/cart';
import logger from 'utils/logger';

import AddToCart from './add-to-cart';
import { StyledButtonBox, StyledRecentOrderCard } from './styled';

interface RecentOrderCardProps {
  cartEntry: IBackendCartEntries | ICartEntryFragment;
  isOnFavoriteSection?: boolean;
  mappedEntry: IReorderEntry;
  checkingAvailability: boolean;
}

const getDayParts = (
  isUnifiedMenuEnabled: boolean,
  indexedMenu: IndexedMenu<ILocalizedMenuEntry>,
  cartEntry: { sanityId: string },
  menuObject?: unknown
): string[] => {
  // dayParts to lower case and skip duplicated if necessary
  const normalize = (parts: ReadonlyArray<string | null | undefined>): string[] => {
    return [...new Set(parts.filter(Boolean).map(p => String(p).toLocaleLowerCase()))];
  };

  // One Menu
  if (isUnifiedMenuEnabled && !!indexedMenu) {
    try {
      const menuEntry = indexedMenu.get(cartEntry.sanityId);
      const dayParts = menuEntry?.dayParts ?? [];
      return normalize(dayParts);
    } catch (error) {
      logger.error({
        error,
        message: `Failed trying to get dayParts from entry: ${cartEntry.sanityId}`,
      });
      return [];
    }
  }

  // Legacy
  if (menuObject) {
    const dayParts = getMenuItemDayParts(menuObject as IMenuObjectWithDaypart);
    return normalize(dayParts);
  }

  return [];
};

export const RecentOrderCard: React.FC<React.PropsWithChildren<RecentOrderCardProps>> = ({
  cartEntry,
  mappedEntry,
  isOnFavoriteSection,
  checkingAvailability,
}) => {
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { indexedMenu, isLoading: isOneMenuLoading } = useUnifiedMenuContext();
  const { frontendEntry, itemAvailabilityStatus, menuObject } = mappedEntry;

  const { noStoreSelected } = useStoreContext();
  const { isFoodware } = useFoodware();
  const itemIsFoodware = isFoodware(frontendEntry!);
  const isExtra = cartEntry?.isExtra;

  // Images from favorites are stored in DynamoDB, so we need to fetch them from there since the links can sometimes be outdated.
  // TODO: This is a temporary fix. We should find a better solution, likely by stopping image storage in DynamoDB.
  const { data: itemData } = useGetItemImageQuery({
    variables: {
      id: cartEntry?.sanityId,
    },
  });

  const dayParts = useMemo(() => {
    if (isOneMenuLoading) {
      return [];
    }
    return getDayParts(isUnifiedMenuEnabled, indexedMenu, cartEntry, menuObject);
  }, [cartEntry, indexedMenu, isOneMenuLoading, isUnifiedMenuEnabled, menuObject]);

  if (!cartEntry) {
    return null;
  }

  // It is only available if we were able to load the sanity data and the status is available
  // Or there is not store selected
  const isAvailable = [
    ItemAvailabilityStatus.AVAILABLE,
    ItemAvailabilityStatus.STORE_NOT_SELECTED,
  ].includes(itemAvailabilityStatus);

  // Only get the selection list if modifiers are enabled for recent items
  const itemSelections = frontendEntry?.children ? getSelectionsListFromEntry(frontendEntry) : [];

  const showFavoritesButton = !isOnFavoriteSection && !isExtra;

  let amplitudeEvent = CustomEventNames.ADD_RECENT_ITEM_TO_CART;

  if (isOnFavoriteSection) {
    amplitudeEvent = CustomEventNames.ADD_FAVORITE_TO_CART;
  }

  return (
    <StyledRecentOrderCard>
      <Product
        loading={!cartEntry || !frontendEntry}
        image={JSON.stringify(itemData?.Item?.image) ?? ''}
        name={frontendEntry?.name}
        quantity={cartEntry.quantity}
        selections={itemSelections}
        displayMode="list"
        note={
          (!noStoreSelected || itemIsFoodware) &&
          !isAvailable && (
            <AvailabilityStatus
              availabilityStatus={itemAvailabilityStatus}
              dayParts={dayParts}
              hideWhenAvailable
              name={frontendEntry?.name}
            />
          )
        }
        quantityBadgeTextStyle={{
          color: Styles.color.white,
        }}
        quantityBadgeStyle={{
          top: '-10px',
          height: '20px',
          width: '20px',
          justifyContent: 'center',
        }}
      />
      <StyledButtonBox>
        {frontendEntry && showFavoritesButton && <FavoriteEntryButton cartEntry={frontendEntry} />}
        {frontendEntry && (
          <AddToCart
            disabled={(!noStoreSelected && !isAvailable) || checkingAvailability || itemIsFoodware}
            cartEntry={frontendEntry}
            amplitudeEvent={amplitudeEvent}
          />
        )}
      </StyledButtonBox>
    </StyledRecentOrderCard>
  );
};
